import { GeneralError, LoginWithEmailUseCase } from '51trips-frontend-core'
import { FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

import IdeaIcon from '../../assets/icons/idea.svg?react'
import { SendMagicLinkUseCase } from '../../domain/useCases/auth/SendMagicLinkUseCase'
import { Button } from '../Button'
import { FormGroup } from '../FormGroup'
import { Input } from '../Input'
import { AuthBackButton } from './AuthBackButton'
import { AuthErrorBanner } from './AuthErrorBanner'
import { AuthMethodOption } from './AuthMethodOption'

interface EmailPasswordFormProps {
  loginWithEmailUseCase: LoginWithEmailUseCase
  sendMagicLinkUseCase: SendMagicLinkUseCase
  onSuccess: () => void
  onMagicLinkSent: (email: string) => void
  onBack: () => void
}

export const EmailPasswordForm: React.FC<EmailPasswordFormProps> = ({
  loginWithEmailUseCase,
  sendMagicLinkUseCase,
  onSuccess,
  onMagicLinkSent,
  onBack
}) => {
  const { t } = useTranslation()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSendingMagicLink, setIsSendingMagicLink] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()

    if (isSubmitting || !email || !password) {
      return
    }

    setIsSubmitting(true)
    setErrorMessage(undefined)

    loginWithEmailUseCase.login(email, password).subscribe({
      next: () => {
        setIsSubmitting(false)
        onSuccess()
      },
      error: (error: GeneralError) => {
        const errorCode = (error.underlyingError as { code?: string } | undefined)?.code
        if (errorCode === 'auth/user-not-found' || errorCode === 'auth/wrong-password') {
          setErrorMessage(t('userNotFoundWrongPassword', { ns: 'auth' }))
        } else {
          setErrorMessage(t('magicLink.sendError', { ns: 'auth' }))
        }
        setIsSubmitting(false)
      }
    })
  }

  const handleSendMagicLink = () => {
    if (isSendingMagicLink || isSubmitting || !email) {
      return
    }

    setIsSendingMagicLink(true)
    setErrorMessage(undefined)

    sendMagicLinkUseCase.sendMagicLink(email).subscribe({
      next: () => {
        setIsSendingMagicLink(false)
        onMagicLinkSent(email)
      },
      error: (error: GeneralError) => {
        setErrorMessage(error.message || t('magicLink.sendError', { ns: 'auth' }))
        setIsSendingMagicLink(false)
      }
    })
  }

  return (
    <div className="flex flex-col gap-5">
      <AuthBackButton onClick={onBack} label={t('loginMethods.otherMethods', { ns: 'auth' })} />

      <div className="flex flex-col gap-1.5">
        <h1 className="text-[21px] font-semibold tracking-tight text-neutralBlack-900">
          {t('emailLogin.title', { ns: 'auth' })}
        </h1>
        <p className="text-[13.5px] text-neutralBlack-500">
          {t('emailLogin.subtitle', { ns: 'auth' })}
        </p>
      </div>

      {errorMessage ? <AuthErrorBanner message={errorMessage} /> : null}

      <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
        <FormGroup label={t('emailInput.label', { ns: 'auth' })}>
          <Input
            value={email}
            onChange={(event: { target: { value: string } }) => setEmail(event.target.value)}
            block
            placeholder={t('emailInput.placeholder', { ns: 'auth' })}
            type="email"
            autoComplete="email"
            required
          />
        </FormGroup>

        <FormGroup label={t('passwordInput.label', { ns: 'auth' })}>
          <Input
            value={password}
            onChange={(event: { target: { value: string } }) => setPassword(event.target.value)}
            block
            placeholder={t('passwordInput.placeholderLogin', { ns: 'auth' })}
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            required
            suffix={
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="text-xs font-medium text-neutralBlack-500 hover:text-neutralBlack-900 cursor-pointer mr-3"
              >
                {showPassword
                  ? t('passwordInput.hide', { ns: 'auth' })
                  : t('passwordInput.show', { ns: 'auth' })}
              </button>
            }
          />
        </FormGroup>

        <div className="flex flex-col gap-3.5 mt-1.5">
          <Button
            type="submit"
            variant="primary"
            size="large"
            label={t('login', { ns: 'auth' })}
            disabled={isSubmitting}
            block
          />

          <div className="flex items-center gap-3">
            <span className="flex-1 h-px bg-neutralBlack-200" />
            <span className="text-xs text-neutralBlack-400">{t('orLoginWith', { ns: 'auth' })}</span>
            <span className="flex-1 h-px bg-neutralBlack-200" />
          </div>

          <AuthMethodOption
            icon={IdeaIcon}
            label={t('magicLink.send', { ns: 'auth' })}
            onClick={handleSendMagicLink}
            disabled={isSendingMagicLink || !email}
          />

          <p className="text-center text-xs text-neutralBlack-400">
            {t('magicLink.description', { ns: 'auth' })}
          </p>
        </div>
      </form>
    </div>
  )
}
